import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Icon from '../../components/AppIcon';

import DepartmentProfileStep from './components/DepartmentProfileStep';
import EmployeeImportStep from './components/EmployeeImportStep';
import ComplianceConfigStep from './components/ComplianceConfigStep';
import TeamAccessStep from './components/TeamAccessStep';

const GovernmentDepartmentSetupWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [setupData, setSetupData] = useState({
    departmentProfile: {},
    employeeData: [],
    complianceSettings: {},
    teamAccess: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = [
    { number: 1, title: 'Department Profile', icon: 'Building2', description: 'Basic department information' },
    { number: 2, title: 'Employee Import', icon: 'Users', description: 'Import employee data' },
    { number: 3, title: 'Compliance Configuration', icon: 'Shield', description: 'Configure compliance settings' },
    { number: 4, title: 'Team Access Setup', icon: 'Key', description: 'Set up team permissions' }
  ];

  const handleStepComplete = (stepNumber, data) => {
    setSetupData(prev => {
      const updatedData = { ...prev };
      switch (stepNumber) {
        case 1:
          updatedData.departmentProfile = data;
          break;
        case 2:
          updatedData.employeeData = data;
          break;
        case 3:
          updatedData.complianceSettings = data;
          break;
        case 4:
          updatedData.teamAccess = data;
          break;
        default:
          break;
      }
      return updatedData;
    });

    if (stepNumber < 4) {
      setCurrentStep(stepNumber + 1);
    } else {
      handleFinalSubmit();
    }
  };

  const handleFinalSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Final validation and setup completion
      console.log('Setup completed with data:', setupData);
      
      // Navigate to dashboard after successful setup
      setTimeout(() => {
        navigate('/compliance-overview');
      }, 1500);
    } catch (err) {
      setError(err?.message || 'Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel the setup? All progress will be lost.')) {
      navigate('/product-demo-onboarding-hub');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <DepartmentProfileStep
            data={setupData?.departmentProfile}
            onComplete={(data) => handleStepComplete(1, data)}
            onCancel={handleCancel}
          />
        );
      case 2:
        return (
          <EmployeeImportStep
            data={setupData?.employeeData}
            departmentInfo={setupData?.departmentProfile}
            onComplete={(data) => handleStepComplete(2, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 3:
        return (
          <ComplianceConfigStep
            data={setupData?.complianceSettings}
            departmentInfo={setupData?.departmentProfile}
            onComplete={(data) => handleStepComplete(3, data)}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );
      case 4:
        return (
          <TeamAccessStep
            data={setupData?.teamAccess}
            departmentInfo={setupData?.departmentProfile}
            employeeData={setupData?.employeeData}
            onComplete={(data) => handleStepComplete(4, data)}
            onBack={handleBack}
            onCancel={handleCancel}
            loading={loading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center mb-4">
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 p-3 rounded-xl shadow-lg">
              <Icon name="Sparkles" size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Public Sector & Private Sector Setup Wizard
          </h1>
          <p className="text-lg text-gray-600">
            Configure your Papua New Guinea organization in under 10 minutes with our guided setup flow
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.number}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                        currentStep > step?.number
                          ? 'bg-emerald-600 text-white'
                          : currentStep === step?.number
                          ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white shadow-lg scale-110'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {currentStep > step?.number ? (
                        <Icon name="Check" size={24} />
                      ) : (
                        <Icon name={step?.icon} size={24} />
                      )}
                    </div>
                    <div className="text-center">
                      <p
                        className={`text-sm font-semibold ${
                          currentStep >= step?.number ? 'text-emerald-700' : 'text-gray-500'
                        }`}
                      >
                        {step?.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{step?.description}</p>
                    </div>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className="flex-1 h-1 mx-4 mb-8">
                      <div
                        className={`h-full rounded transition-all ${
                          currentStep > step?.number ? 'bg-emerald-600' : 'bg-gray-200'
                        }`}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="max-w-4xl mx-auto mb-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
              <Icon name="AlertCircle" size={20} className="text-red-600 mr-3 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800">Setup Error</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Step Content */}
        <div className="max-w-4xl mx-auto">
          {renderStepContent()}
        </div>

        {/* Quick Start Templates Info */}
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-gradient-to-r from-amber-50 to-emerald-50 rounded-xl p-6 border border-amber-200">
            <div className="flex items-start">
              <Icon name="Lightbulb" size={24} className="text-amber-600 mr-3 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Quick-Start Templates Available
                </h3>
                <p className="text-sm text-gray-700 mb-3">
                  Accelerate your setup with pre-configured templates for common PNG government departments:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-emerald-700 text-sm">Health Department</p>
                    <p className="text-xs text-gray-600 mt-1">Pre-configured compliance for healthcare workers</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-emerald-700 text-sm">Education Department</p>
                    <p className="text-xs text-gray-600 mt-1">Teacher-specific compliance settings</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="font-semibold text-emerald-700 text-sm">Finance Department</p>
                    <p className="text-xs text-gray-600 mt-1">Financial sector compliance templates</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GovernmentDepartmentSetupWizard;